import type { EChartsOption } from 'echarts';
import type { ChartOptionBuildOptions } from './chart-option-chrome';
import {
  buildAxisCommon,
  buildSharedChrome,
  buildStableGrid,
  buildTooltipStyle,
  pickColor,
} from './chart-option-chrome';
import { resolveEchartsThemeTokens } from './resolve-echarts-theme-tokens';

/**
 * Input for {@link createScatterChartOptions}.
 *
 * Each point is `[x, y]` or `[x, y, size]` for bubble sizing.
 *
 * @example
 * const input: ScatterChartInput = {
 *   series: [{ name: 'Factories', points: [[10, 20], [30, 40, 12]] }],
 * };
 */
export type ScatterChartInput = {
  series: readonly {
    name: string;
    points: readonly (readonly number[])[];
  }[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  colors?: string[];
};

/**
 * Generate an ECharts scatter (or bubble) chart.
 *
 * @param input Scatter series with `[x, y]` or `[x, y, size]` points. {@link ScatterChartInput}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A bare {@link EChartsOption}.
 * @example
 * const opt = createScatterChartOptions({
 *   series: [{ name: 'Factories', points: [[10, 20], [30, 40, 12]] }],
 * });
 */
export function createScatterChartOptions(
  input: ScatterChartInput,
  buildOptions: ChartOptionBuildOptions = {}
): EChartsOption {
  const theme = buildOptions.theme ?? resolveEchartsThemeTokens();
  const palette = input.colors ?? theme.palette;
  const axis = buildAxisCommon(theme);

  return {
    ...buildSharedChrome(theme, buildOptions.showToolbox === true),
    tooltip: {
      ...buildTooltipStyle(theme),
      trigger: 'item',
    },
    legend: {
      show: true,
      textStyle: { color: theme.mutedColor },
    },
    grid: buildStableGrid({ left: 8, right: 16, top: 32, bottom: 8 }),
    xAxis: {
      type: 'value',
      name: input.xAxisLabel,
      ...axis,
    },
    yAxis: {
      type: 'value',
      name: input.yAxisLabel,
      ...axis,
    },
    series: input.series.map((entry, index) => ({
      name: entry.name,
      type: 'scatter',
      data: entry.points.map((point) => [...point]),
      symbolSize: (value: number | number[]) => {
        if (Array.isArray(value) && typeof value[2] === 'number') {
          return Math.max(8, value[2]);
        }
        return 12;
      },
      itemStyle: { color: pickColor(palette, index), opacity: 0.85 },
    })),
  };
}
